import type { HandlingTimePerPersonRow } from './handlingTime.api';

/**
 * Story 31.2 — het rekenwerk onder de cijfers boven de tabel, uit dezelfde regels als de tabel zelf.
 *
 * De bestandsnaam is die van de vier kerncijfers waarvoor dit gebouwd is. Die vier zijn met story
 * 33.3 drie contextkaarten geworden en het losse groepsgemiddelde per stuk is toen vervallen; het
 * rekenwerk hieronder is gebleven en voedt nu `Contextkaarten.tsx`. De naam is niet meegegaan omdat
 * `STAP_VASTLEGGEN` en `STAP_VALIDEREN` hiervandaan komen en door de tabel en de reeks gelezen
 * worden — hernoemen raakt meer dan het opheldert.
 *
 * Hier staat uitsluitend rekenwerk, en dat is met opzet: de scherpste regels van deze story zijn
 * rekenregels en geen opmaak. Ze staan hieronder elk bij het stuk dat ze bewaakt.
 *
 * De optelling gebeurt HIER en niet bij de bron, om dezelfde reden waarom het gemiddelde per stuk
 * in de tabel hier gedeeld wordt: een lezer moet kunnen narekenen wat hij ziet. Zou de bron de
 * kaarten meesturen, dan konden de kaarten en de tabel uit elkaar gaan lopen zonder dat iemand het
 * merkt.
 */

/** De twee stappen, zoals de bron ze noemt. Vastleggen vóór valideren, altijd in die volgorde. */
export const STAP_VASTLEGGEN = 'processing';
export const STAP_VALIDEREN = 'validation';

/**
 * De vier getallen per stap.
 *
 * Sinds story 33.3 leest het scherm van deze vier alleen nog `aandeel`: de kaart met het gemiddelde
 * per stuk is toen vervallen. De andere drie blijven staan, en dat is geen slordigheid — de
 * acceptatiecriteria van die story (AC-16) pinnen juist de toetsen erop vast: dat `null` geen nul
 * wordt, dat de som over de stappen behandelingen telt en geen stuks, en dat een regel zonder
 * `handlings` buiten de berekening valt. `tabel.test.ts` legt de kolomtotalen ertegenaan.
 */
export interface StapCijfers {
  /** Mensminuten in deze stap. `null` — nooit `0` — zodra er niets gemeten is. */
  minuten: number | null;
  /** Het aantal uitgevoerde behandelingen in deze stap. Nadrukkelijk geen ontdubbelde stuks. */
  behandelingen: number | null;
  /** Minuten van deze stap gedeeld door de behandelingen van DÍE stap. */
  gemiddeldePerStuk: number | null;
  /** Het aandeel van deze stap in de totale behandeltijd, als percentage. */
  aandeel: number | null;
}

export interface Kerncijfers {
  totaleMinuten: number | null;
  vastleggen: StapCijfers;
  valideren: StapCijfers;
  /**
   * De som van de behandelingen over de twee stappen.
   *
   * Dit is uitdrukkelijk GEEN aantal stuks: de bron telt stuks ontdubbeld, en een stuk dat door
   * beide stappen ging telt hier dus twee keer. Dat was een fout in de eerste mock-up en hij is
   * daar gecorrigeerd; de kaart draagt de uitleg mee, want het getal is anders niet te plaatsen.
   */
  uitgevoerdeBehandelingen: number | null;
}

/**
 * Optellen zonder van "niets" een nul te maken.
 *
 * `null` plus `null` blijft `null`, en dat is het hele punt: een stap waarin niemand een minuut
 * droeg heeft geen meting, en dat is iets anders dan een meting die op nul uitkwam. Elke plek waar
 * dit onderscheid platgeslagen wordt levert een streepje op dat als nul leest.
 */
function telOp(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return a + b;
}

/** De optelling van één stap over alle personen. */
function somVoorStap(rijen: HandlingTimePerPersonRow[], stap: string): { minuten: number | null; behandelingen: number | null } {
  let minuten: number | null = null;
  let behandelingen: number | null = null;

  for (const rij of rijen) {
    // Een antwoord van vóór story 31.1 draagt de uitsplitsing niet. Dan is er voor deze kaarten
    // niets te rekenen, en dat is iets anders dan nul — de regel valt hier gewoon buiten.
    for (const eigen of rij.handlings ?? []) {
      if (eigen.handling !== stap) continue;
      minuten = telOp(minuten, eigen.minutes);
      behandelingen = telOp(behandelingen, eigen.tradeItems);
    }
  }

  return { minuten, behandelingen };
}

/** Minuten gedeeld door behandelingen, en niets zodra er niets te delen valt. */
function gemiddelde(minuten: number | null, behandelingen: number | null): number | null {
  if (minuten === null || behandelingen === null || behandelingen <= 0) return null;
  return minuten / behandelingen;
}

/**
 * De cijfers over een lijst persoonsregels, waaruit de drie contextkaarten zijn opgebouwd.
 *
 * Twee gemiddelden, nooit één (epic 31 §3): vastleggen en valideren zijn ongelijksoortig werk, en
 * één gemiddelde over beide samen beweegt zodra de verhouding verschuift zonder dat iemand sneller
 * of langzamer werkte. Daarom staat er hier ook geen samengetrokken gemiddelde — niet als veld en
 * niet als tussenstap.
 */
export function berekenKerncijfers(rijen: HandlingTimePerPersonRow[]): Kerncijfers {
  const v = somVoorStap(rijen, STAP_VASTLEGGEN);
  const c = somVoorStap(rijen, STAP_VALIDEREN);
  const totaleMinuten = telOp(v.minuten, c.minuten);

  // Het aandeel bestaat alleen wanneer er iets te verdelen is. Is de ene stap leeg en de andere
  // niet, dan staat er 100% naast een streepje — nadrukkelijk niet 100% naast 0% (AC-3).
  const aandeelVan = (minuten: number | null): number | null =>
    minuten === null || totaleMinuten === null || totaleMinuten <= 0 ? null : (minuten / totaleMinuten) * 100;

  return {
    totaleMinuten,
    vastleggen: {
      minuten: v.minuten,
      behandelingen: v.behandelingen,
      gemiddeldePerStuk: gemiddelde(v.minuten, v.behandelingen),
      aandeel: aandeelVan(v.minuten),
    },
    valideren: {
      minuten: c.minuten,
      behandelingen: c.behandelingen,
      gemiddeldePerStuk: gemiddelde(c.minuten, c.behandelingen),
      aandeel: aandeelVan(c.minuten),
    },
    uitgevoerdeBehandelingen: telOp(v.behandelingen, c.behandelingen),
  };
}

/**
 * Hoe een kerncijfer zich verhoudt tot dezelfde meting over de vorige even lange periode.
 *
 * `geen` — er valt niets te vergelijken, bijvoorbeeld omdat de tweede aanroep er nog niet is of
 * mislukte, of omdat er nú niets gemeten is.
 * `toen-niets-gemeten` — de vorige periode droeg geen meting. Er komt dan géén percentage: "oneindig
 * veel meer dan nul" is geen stuurinformatie (AC-9).
 */
export interface Vergelijking {
  soort: 'geen' | 'toen-niets-gemeten' | 'vrijwel-gelijk' | 'meer' | 'minder';
  waarde?: number;
  eenheid?: 'procent' | 'procentpunt';
}

/** Onder deze grens heet een verschil "vrijwel gelijk" in plaats van een getal met één decimaal. */
const VRIJWEL_GELIJK_PROCENT = 0.5;
const VRIJWEL_GELIJK_PROCENTPUNT = 0.05;

/** Een gewoon getal tegenover hetzelfde getal van toen, in procenten. */
export function vergelijkGetal(nu: number | null, eerder: number | null): Vergelijking {
  // NIETS TEGENOVER NIETS IS GEEN VERGELIJKING, en die volgorde is een reviewbevinding van
  // 27 augustus 2026. Stond deze regel eronder, dan meldde een scherm waarin in beide periodes
  // niets gemeten is dat er "toen niets gemeten" was — waar dat evengoed voor nu geldt. Er valt
  // dan gewoon niets te vergelijken, en dat is wat er hoort te staan.
  if (nu === null && eerder === null) return { soort: 'geen' };

  // Een gemeten NUL en een ontbrekende meting lopen hier samen, en dat is geen slordigheid: een
  // percentage tegenover nul bestaat niet, hoe je er ook naar kijkt. Voor de minuten kán het geval
  // sowieso niet ontstaan — de bron geeft daar  en nooit  (FR25).
  if (eerder === null || eerder === 0) return { soort: 'toen-niets-gemeten' };
  if (nu === null) return { soort: 'geen' };

  const procent = ((nu - eerder) / eerder) * 100;
  if (Math.abs(procent) < VRIJWEL_GELIJK_PROCENT) return { soort: 'vrijwel-gelijk' };
  return { soort: procent > 0 ? 'meer' : 'minder', waarde: Math.abs(procent), eenheid: 'procent' };
}

/**
 * Een aandeel tegenover hetzelfde aandeel van toen, in PROCENTPUNTEN.
 *
 * Van 55% naar 57% is twee procentpunt meer, niet 3,6% meer (AC-10). Het tweede getal klopt
 * rekenkundig en zegt niets: het is de verandering van een verandering, en niemand stuurt daarop.
 */
export function vergelijkAandeel(nu: number | null, eerder: number | null): Vergelijking {
  // NIETS TEGENOVER NIETS IS GEEN VERGELIJKING, precies zoals hierboven, en deze regel ontbrak hier
  // tot een codereviewbevinding van 30 augustus 2026. Zonder hem meldde een scherm waarin in BEIDE
  // periodes niets gemeten is dat er "toen niets gemeten" was — waar dat evengoed voor nu geldt. Dat
  // is vandaag de normale toestand op stand "Beide", en de contextkaarten van story 33.3 zetten die
  // mededeling er als losse regel onder; hij stond er dus terwijl er niets te melden viel.
  if (nu === null && eerder === null) return { soort: 'geen' };
  if (eerder === null) return { soort: 'toen-niets-gemeten' };
  if (nu === null) return { soort: 'geen' };

  const punten = nu - eerder;
  if (Math.abs(punten) < VRIJWEL_GELIJK_PROCENTPUNT) return { soort: 'vrijwel-gelijk' };
  return { soort: punten > 0 ? 'meer' : 'minder', waarde: Math.abs(punten), eenheid: 'procentpunt' };
}
